import './RegistrationStatusPage.css';
import {useEffect, useLayoutEffect, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {PUBLIC_API_V1_BASE} from '../../config/publicApi';
import {requestFormAdminNotify} from '../../utils/formAdminNotify';
import {getResolvedRegistrationPaymentButtons, sanitizePaymentButtonBackgroundForCss} from '../../utils/activityRegistrationPayment';
import CircularPageLoader from '../common/CircularPageLoader';

const APPROVAL_LABELS = {
    pending: 'Beklemede',
    approved: 'Onaylandı',
    rejected: 'Reddedildi',
    cancelled: 'İptal edildi',
};

function statusStripModifier(approvalStatus) {
    const s = String(approvalStatus || '').toLowerCase();
    if (s === 'approved') return 'registration-status-strip--approved';
    if (s === 'rejected') return 'registration-status-strip--rejected';
    if (s === 'cancelled') return 'registration-status-strip--cancelled';
    return 'registration-status-strip--pending';
}

function pickDataField(data, camel, snake) {
    if (!data || typeof data !== 'object') return undefined;
    if (Object.prototype.hasOwnProperty.call(data, camel) && data[camel] !== undefined && data[camel] !== null) {
        return data[camel];
    }
    if (Object.prototype.hasOwnProperty.call(data, snake) && data[snake] !== undefined && data[snake] !== null) {
        return data[snake];
    }
    return data[camel] ?? data[snake];
}

export default function RegistrationStatusPage() {
    const location = useLocation();
    const params = useParams();
    const rawParam = (params.referenceCode || '').trim();
    const referenceCode = (() => {
        try {
            return decodeURIComponent(rawParam).trim();
        } catch {
            return rawParam;
        }
    })();

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const referenceCodeLooksValid =
        Boolean(referenceCode) && /^[A-Za-z0-9_-]{4,40}$/.test(referenceCode);
    const showSubmitThanks =
        referenceCodeLooksValid && location.state?.fromRegistrationSubmit === true;
    const adminNotify = location.state?.adminNotify;

    /** Iframe / long previous screen: always show this view from the top. */
    useLayoutEffect(() => {
        const scrollTop = () => {
            window.scrollTo({top: 0, left: 0, behavior: 'auto'});
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
        };
        scrollTop();
        const raf = requestAnimationFrame(() => {
            scrollTop();
        });
        return () => cancelAnimationFrame(raf);
    }, [referenceCode, location.key, location.pathname]);

    useEffect(() => {
        if (loading) return;
        window.scrollTo({top: 0, left: 0, behavior: 'auto'});
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
    }, [loading, referenceCode]);

    useEffect(() => {
        if (!showSubmitThanks || !referenceCodeLooksValid || loading || !data || !adminNotify) {
            return;
        }
        requestFormAdminNotify(adminNotify);
    }, [showSubmitThanks, referenceCodeLooksValid, loading, data, adminNotify]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!referenceCode || !/^[A-Za-z0-9_-]{4,40}$/.test(referenceCode)) {
                setError('Geçersiz referans kodu.');
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(
                    `${PUBLIC_API_V1_BASE}/form/register/registration-status/${encodeURIComponent(referenceCode)}`
                );
                const body = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (!res.ok) {
                    if (res.status === 401) {
                        setError(
                            'API bu isteği reddetti (kimlik doğrulama). Yerel geliştirmede campego-api ve ' +
                                'campego-forms aynı adresi kullanmalı; ayrıca auth middleware’in /api/v1/form yollarını açtığından emin olun.'
                        );
                    } else {
                        setError(
                            res.status === 404
                                ? 'Bu referans kodu ile kayıt bulunamadı.'
                                : body.error || 'Bilgiler yüklenirken bir hata oluştu.'
                        );
                    }
                    setData(null);
                } else {
                    setData(body);
                }
            } catch (e) {
                if (!cancelled) {
                    setError('Sunucuya bağlanılamadı. Lütfen daha sonra tekrar deneyin.');
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [referenceCode]);

    const selectedDate = pickDataField(data, 'selectedDateOption', 'selected_date_option');
    const selectedParticipant = pickDataField(data, 'selectedParticipantOption', 'selected_participant_option');
    const selectedStay = pickDataField(data, 'selectedStayOption', 'selected_stay_option');
    const paymentDetails = pickDataField(data, 'paymentDetails', 'payment_details');

    let paymentButtonsToRender = [];
    if (
        data &&
        paymentDetails != null &&
        selectedDate != null &&
        String(selectedDate).trim() !== '' &&
        selectedParticipant != null &&
        String(selectedParticipant).trim() !== '' &&
        selectedStay != null &&
        String(selectedStay).trim() !== ''
    ) {
        paymentButtonsToRender = getResolvedRegistrationPaymentButtons(
            paymentDetails,
            selectedDate,
            selectedParticipant,
            selectedStay
        ).filter((b) => b.enabled && b.paymentLink);
    }

    if (paymentButtonsToRender.length === 0 && data) {
        const opts = Array.isArray(data.paymentOptions)
            ? data.paymentOptions
            : Array.isArray(data.payment_options)
              ? data.payment_options
              : [];
        paymentButtonsToRender = opts.filter(
            (o) =>
                o &&
                o.enabled !== false &&
                o.paymentLink &&
                /^https?:\/\//i.test(String(o.paymentLink).trim())
        );
    }

    if (paymentButtonsToRender.length === 0 && data) {
        const pl = pickDataField(data, 'paymentLink', 'payment_link');
        const amt = pickDataField(data, 'paymentAmount', 'payment_amount');
        if (pl && /^https?:\/\//i.test(String(pl).trim())) {
            paymentButtonsToRender = [
                {
                    paymentLink: String(pl).trim(),
                    paymentAmount: amt != null ? String(amt) : '',
                    name: '',
                    color: '#073763',
                    slotLabel: 'Ödemeyi tamamla',
                },
            ];
        }
    }

    const approvalStatusRaw = data
        ? pickDataField(data, 'approvalStatus', 'approval_status')
        : undefined;
    const approvalStatusLower = String(approvalStatusRaw || '').toLowerCase();
    const isPendingApproval = approvalStatusLower === 'pending';
    const isApprovedApproval = approvalStatusLower === 'approved';

    const approvalLabel =
        APPROVAL_LABELS[approvalStatusLower] || approvalStatusRaw || data?.approvalStatus || '—';

    const showPostSubmitHeroPanel =
        showSubmitThanks &&
        referenceCodeLooksValid &&
        !loading &&
        !error &&
        data &&
        typeof data === 'object';

    const showLoadingPanel = loading && !error;

    return (
        <div className="container py-5 registration-status-page">
            <div className="text-center mx-auto" style={{maxWidth: 560}}>
                {showPostSubmitHeroPanel && isPendingApproval && (
                    <div className="registration-submit-next mb-4">
                        <div className="registration-submit-next__icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="34" height="34" fill="none">
                                <path
                                    d="M4 8.5h16v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M4 10.5h16"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M8 14.5h3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <h1 className="registration-submit-next__main-title">Kayıt Başvurunuzu aldık ✅</h1>
                        <p className="registration-submit-next__step-title">Sıradaki Adım: Ödeme</p>
                        <p className="registration-submit-next__subtitle mb-2">
                            Campego&apos;ya gösterdiğiniz ilgi için teşekkür ederiz. Kayıt başvurunuz başarıyla
                            sistemimize ulaşmıştır.
                        </p>
                        <p className="registration-submit-next__subtitle mb-2">
                            Kaydınızın tamamlanabilmesi için bir sonraki adımı gerçekleştirmeniz gerekmektedir. Bilgi
                            ve süreçle ilgili detaylar için ekibimiz en kısa sürede sizinle iletişime geçecektir.
                        </p>
                        <p className="registration-submit-next__note mb-0">
                            Bu adımı daha önce tamamladıysanız lütfen bu bilgilendirmeyi dikkate almayınız.
                        </p>
                    </div>
                )}

                {showPostSubmitHeroPanel && isApprovedApproval && (
                    <div className="registration-submit-thanks mb-4">
                        <div className="registration-submit-thanks__icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="36" height="36" fill="none">
                                <path
                                    d="M6 12.5 10.5 17 18 8"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h1 className="registration-submit-thanks__title">Teşekkürler!</h1>
                        <p className="registration-submit-thanks__subtitle mb-0">
                            Başvurunuz onaylandı. Kaydınız tamamlandı.
                        </p>
                    </div>
                )}

                {showPostSubmitHeroPanel && !isPendingApproval && !isApprovedApproval && (
                    <div className="registration-submit-neutral mb-4">
                        <h1 className="registration-submit-neutral__title">Başvurunuz kaydedildi</h1>
                        <p className="registration-submit-neutral__subtitle mb-0">
                            Güncel kayıt durumunuzu ve bilgilerinizi aşağıda görebilirsiniz.
                        </p>
                    </div>
                )}

                {showLoadingPanel && (
                    <CircularPageLoader
                        title="Yükleniyor…"
                        hint="Kayıt bilgileriniz getiriliyor."
                        ariaLabel="Kayıt bilgileri yükleniyor"
                    />
                )}

                {!loading && error && <p className="text-danger fw-medium">{error}</p>}

                {!loading && !error && data && (
                    <>
                        <div
                            className={`registration-status-strip mx-auto mb-4 ${statusStripModifier(
                                data.approvalStatus
                            )}`}
                        >
                            <span className="registration-status-strip__label">Kayıt durumu</span>
                            <span className="registration-status-strip__value">{approvalLabel}</span>
                        </div>

                        <div className="mx-auto bg-light py-4 px-4 rounded-3 mb-4 text-start">
                            <div className="row mb-2">
                                <div className="col-5 text-muted small">Referans kodu</div>
                                <div className="col-7 fw-bold text-break">{data.referenceCode}</div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-5 text-muted small">Program</div>
                                <div className="col-7 fw-semibold">{data.activityTitle}</div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-5 text-muted small">Onay durumu</div>
                                <div className="col-7">{approvalLabel}</div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-5 text-muted small">Tarih</div>
                                <div className="col-7">{data.selectedDateOption || '—'}</div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-5 text-muted small">Kişi seçeneği</div>
                                <div className="col-7">{data.selectedParticipantOption || '—'}</div>
                            </div>
                            <div className="row">
                                <div className="col-5 text-muted small">Konaklama</div>
                                <div className="col-7">{data.selectedStayOption || '—'}</div>
                            </div>
                        </div>

                        {paymentButtonsToRender.length > 0 ? (
                            <div className="registration-payment-stack mb-4">
                                {isPendingApproval ? (
                                    <p className="registration-submit-next__subtitle text-center mb-3">
                                        Dilerseniz aşağıdaki seçenekler üzerinden size en uygun olan ödeme yöntemini
                                        kullanarak kaydınızı hemen tamamlayabilirsiniz.
                                    </p>
                                ) : null}
                                {paymentButtonsToRender.map((opt, idx) => {
                                    const href =
                                        opt.paymentLink &&
                                        /^https?:\/\//i.test(String(opt.paymentLink).trim())
                                            ? String(opt.paymentLink).trim()
                                            : '';
                                    if (!href) return null;
                                    const label =
                                        (opt.name && String(opt.name).trim()) ||
                                        opt.slotLabel ||
                                        'Ödemeyi tamamla';
                                    const bg = sanitizePaymentButtonBackgroundForCss(opt.color);
                                    return (
                                        <div
                                            key={idx}
                                            className={`registration-payment-option text-center${
                                                idx > 0 ? ' registration-payment-option--spaced' : ''
                                            }`}
                                        >
                                            <a
                                                href={href}
                                                className="btn text-white px-4 py-2 rounded-3 fw-bold d-inline-block fs-6"
                                                style={{
                                                    backgroundColor: bg,
                                                    minWidth: '200px',
                                                }}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {label}
                                            </a>
                                            {opt.paymentAmount ? (
                                                <p className="fs-6 text-secondary mt-3 mb-0">
                                                    Ödeme tutarı:{' '}
                                                    <span className="fw-bold text-dark">
                                                        ₺ {opt.paymentAmount}
                                                    </span>
                                                </p>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}
